/**
 * Letters shown before anybody receives them.
 *
 * Two screens of the administration show a letter the portal sends on its
 * own rather than one somebody writes on the mail screen, and both the same
 * way: a row naming the letter, and a button opening a window with it.
 *
 * - In the settings the window holds the letter which confirms an address,
 *   drawn with the page, a tab per language.
 * - On the announcements it holds the copy a player gets by mail, drawn from
 *   what the form holds at the moment the window is opened.
 *
 * A letter drawn from a form outside any window is drawn again as the form
 * is typed instead.
 *
 * Both hold the letter in a frame of its own, which is only as tall as the
 * letter once it is on screen - so a frame is measured again whenever it is
 * shown, not only when it loads.
 *
 * ```html
 * <button data-letter-open="verification-letter">…</button>
 * <dialog id="verification-letter" data-letter-dialog>… <iframe data-mail-frame> …</dialog>
 *
 * <dialog id="announcement-letter" data-letter-dialog>
 *     <div data-letter-live data-letter-slot data-letter-url="/admin/announcements/preview"
 *         data-letter-form="announcement-form" data-letter-fields="title,message,url"></div>
 * </dialog>
 * ```
 */

import { fitFrame } from "./mailComposer";
import { dialogsWork } from "./modal";
import { queryAll } from "../core/dom";
import { postJson } from "../core/http";
import { text } from "../core/i18n";
import type { PreviewResponse } from "../core/types";

/** How long typing has to pause before the letter is drawn again, in milliseconds. */
export const LIVE_DELAY = 350;

const ERROR_FALLBACK = "Náhľad sa nepodarilo načítať, skúste to prosím znova.";

/** What a window is told when it opens, so a letter inside it can draw itself. */
export const OPENED = "letter:open";

/** Measure every letter inside `scope` again, now that it may be on screen. */
export function fitLetters(scope: ParentNode): void {
    for (const frame of queryAll<HTMLIFrameElement>("iframe[data-mail-frame]", scope)) {
        fitFrame(frame);
    }
}

/** Wire the button which opens the window of one letter. */
export function bindLetterDialog(opener: HTMLElement): void {
    const dialog = document.getElementById(opener.dataset["letterOpen"] ?? "");
    if (!(dialog instanceof HTMLDialogElement)) {
        return;
    }

    opener.addEventListener("click", () => {
        dialog.showModal();
        dialog.dispatchEvent(new CustomEvent(OPENED));
        fitLetters(dialog);
    });

    if (dialog.dataset["letterBound"] === "1") {
        return;
    }

    dialog.dataset["letterBound"] = "1";

    for (const closer of queryAll<HTMLElement>("[data-letter-close]", dialog)) {
        closer.addEventListener("click", () => dialog.close());
    }

    // A tab shows a frame which was hidden until now, and a hidden frame
    // measures nothing. The tabs switch on the same click, so the letter is
    // measured once they have.
    dialog.addEventListener("click", (event: Event) => {
        if (event.target instanceof Element && event.target.closest("[data-tab]")) {
            window.setTimeout(() => fitLetters(dialog), 0);
        }
    });

    // A click on the backdrop lands on the dialog itself, outside its inner box.
    dialog.addEventListener("click", (event: MouseEvent) => {
        if (event.target === dialog) {
            dialog.close();
        }
    });
}

/** Return what the named fields of `form` hold, by name - empty for a field which is not there. */
export function payloadOf(form: HTMLFormElement, fields: readonly string[]): Record<string, string> {
    const payload: Record<string, string> = {};

    for (const name of fields) {
        const field = form.elements.namedItem(name);
        payload[name] =
            field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement
                ? field.value
                : "";
    }

    return payload;
}

/**
 * Keep the letter of one panel in step with the form it previews.
 *
 * Inside a window the letter is drawn each time the window opens, from what
 * the form holds then; nobody sees it while typing, so it is not drawn while
 * typing either. Outside one it is drawn at once and again as the form is typed.
 */
export function bindLiveLetter(panel: HTMLElement): void {
    const endpoint = panel.dataset["letterUrl"] ?? "";
    const form = document.getElementById(panel.dataset["letterForm"] ?? "");
    const slot = panel.matches("[data-letter-slot]") ? panel : panel.querySelector<HTMLElement>("[data-letter-slot]");
    const dialog = panel.closest("dialog");

    if (!endpoint || !(form instanceof HTMLFormElement) || !slot) {
        return;
    }

    const fields = (panel.dataset["letterFields"] ?? "")
        .split(",")
        .map((name) => name.trim())
        .filter((name) => name.length > 0);

    let drawn: string | null = null;
    let timer: number | undefined;
    let controller: AbortController | undefined;

    const draw = (): void => {
        const payload = payloadOf(form, fields);
        const drawing = JSON.stringify(payload);

        // Typing into a field the letter does not show changes nothing in it.
        if (drawing === drawn) {
            return;
        }

        controller?.abort();
        controller = new AbortController();

        void postJson<PreviewResponse>(endpoint, payload, controller.signal)
            .then((answer) => {
                drawn = drawing;
                slot.innerHTML = answer.html;
            })
            .catch((error: unknown) => {
                if (error instanceof DOMException && error.name === "AbortError") {
                    return;
                }

                drawn = null;

                const notice = document.createElement("p");
                notice.className = "field__hint letter-dialog__waiting";
                notice.textContent = text("js.editor.error", ERROR_FALLBACK);
                slot.replaceChildren(notice);
            });
    };

    if (dialog) {
        dialog.addEventListener(OPENED, draw);

        return;
    }

    form.addEventListener("input", () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(draw, LIVE_DELAY);
    });

    draw();
}

/** Wire every letter shown on the current page. */
export function initLetters(): void {
    if (dialogsWork()) {
        for (const opener of queryAll<HTMLElement>("[data-letter-open]")) {
            bindLetterDialog(opener);
        }
    } else {
        // Without windows the button leads nowhere; the settings still work.
        for (const opener of queryAll<HTMLElement>("[data-letter-open]")) {
            opener.hidden = true;
        }
    }

    for (const panel of queryAll<HTMLElement>("[data-letter-live]")) {
        bindLiveLetter(panel);
    }
}
